import React, { useState, useEffect } from 'react';
import API from '../services/api';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer
} from 'recharts';

const RevenueSummaryCard = () => {
  const [summaryData, setSummaryData] = useState({
    today: { billed: 0, collected: 0 },
    week: { billed: 0, collected: 0 },
    month: { billed: 0, collected: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [periodType, setPeriodType] = useState('day'); // 'day', 'week', 'month'
  const [viewType, setViewType] = useState('billed'); // 'billed' or 'collected'
  const [showGraph, setShowGraph] = useState(false);
  const [therapists, setTherapists] = useState([]);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  
  // NEW: Year-over-year comparison state
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonType, setComparisonType] = useState('month'); // 'week', 'month', 'quarter'
  const [showPaymentTypes, setShowPaymentTypes] = useState(true);
  
  // NEW: Period detail drill-down state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedPeriodLabel, setSelectedPeriodLabel] = useState('');
  const [detailViewTab, setDetailViewTab] = useState('sessions'); // 'sessions', 'byTherapist', 'byServiceType'

  useEffect(() => {
    fetchTherapists();
    fetchSummaryData();
  }, []);
  
  useEffect(() => {
    fetchSummaryData(selectedTherapist);
  }, [selectedTherapist]);

  const fetchTherapists = async () => {
    try {
      const res = await API.get('/admin/active-therapists');
      setTherapists(res.data);
    } catch (err) {
      console.error('Error fetching therapists:', err);
    }
  };

  const fetchSummaryData = async (therapistId = null) => {
    try {
      setLoading(true);
      let url = '/admin/revenue-summary';
      if (therapistId) {
        url += `?therapist_id=${therapistId}`;
      }
      const res = await API.get(url);
      setSummaryData(res.data);
    } catch (err) {
      console.error('Error fetching revenue summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoryData = async (type, therapistId = selectedTherapist) => {
    try {
      setHistoryLoading(true);
      setPeriodType(type);
      let url = `/admin/revenue-history?type=${type}`;
      if (therapistId) {
        url += `&therapist_id=${therapistId}`;
      }
      const res = await API.get(url);
      setHistoryData(res.data);
      setShowHistoryModal(true);
    } catch (err) {
      console.error(`Error fetching ${type} history:`, err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // NEW: Fetch year-over-year comparison data
  const fetchComparisonData = async (type = comparisonType, therapistId = selectedTherapist) => {
    try {
      setComparisonLoading(true);
      setComparisonType(type);
      let url = `/admin/revenue-comparison?type=${type}`;
      if (therapistId) {
        url += `&therapist_id=${therapistId}`;
      }
      const res = await API.get(url);
      setComparisonData(res.data);
      setShowComparisonModal(true);
    } catch (err) {
      console.error(`Error fetching ${type} comparison:`, err);
    } finally {
      setComparisonLoading(false);
    }
  };

  const handleCardClick = () => {
    fetchHistoryData('day');
  };

  // NEW: Fetch detailed session data for a specific period
  const fetchPeriodDetails = async (item) => {
    try {
      setDetailLoading(true);
      setSelectedPeriodLabel(getPeriodLabel(item.period));
      setDetailViewTab('sessions');
      
      let startDate, endDate;
      
      if (periodType === 'day') {
        // Single day
        startDate = item.period;
        endDate = item.period;
      } else if (periodType === 'week') {
        // Week range
        startDate = item.period.startDate;
        endDate = item.period.endDate;
      } else if (periodType === 'month') {
        // Full month
        const date = new Date(item.period);
        startDate = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
        endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0];
      }
      
      let url = `/admin/revenue-period-details?start_date=${startDate}&end_date=${endDate}`;
      if (selectedTherapist) {
        url += `&therapist_id=${selectedTherapist}`;
      }
      
      const res = await API.get(url);
      setDetailData(res.data);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error fetching period details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleTherapistChange = (e) => {
    const therapistId = e.target.value === 'all' ? null : e.target.value;
    setSelectedTherapist(therapistId);
    if (showHistoryModal) {
      fetchHistoryData(periodType, therapistId);
    }
    if (showComparisonModal) {
      fetchComparisonData(comparisonType, therapistId);
    }
  };

  const toggleViewType = () => {
    setViewType(viewType === 'billed' ? 'collected' : 'billed');
  };

  const toggleGraph = () => {
    setShowGraph(!showGraph);
  };

  const getPeriodLabel = (period) => {
    switch (periodType) {
      case 'day':
        return new Date(period).toLocaleDateString();
      case 'week':
        const weekStart = new Date(period.startDate);
        const weekEnd = new Date(period.endDate);
        return `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
      case 'month':
        const date = new Date(period);
        return date.toLocaleDateString('default', { month: 'long', year: 'numeric' });
      default:
        return period;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatCurrencyK = (amount) => {
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}k`;
    }
    return formatCurrency(amount);
  };

  // Prepare chart data for regular history view
  const prepareChartData = () => {
    if (periodType === 'day' || periodType === 'month') {
      return historyData.map(item => ({
        name: new Date(item.period).toLocaleDateString('default', { month: 'short', day: 'numeric' }),
        [viewType]: item[viewType]
      })).reverse();
    } 
    return historyData.map(item => ({
      name: `${new Date(item.period.startDate).toLocaleDateString('default', { month: 'short', day: 'numeric' })}`,
      [viewType]: item[viewType]
    })).reverse();
  };

  // NEW: Prepare chart data for year-over-year comparison
  const prepareComparisonChartData = () => {
    if (!comparisonData) return [];
    
    return comparisonData.periods.map(period => {
      const dataPoint = {
        name: period.label,
      };

      if (showPaymentTypes) {
        // Stacked bars with payment type breakdown
        dataPoint[`${comparisonData.previousYear}_cash`] = period.previousYear.cash;
        dataPoint[`${comparisonData.previousYear}_check`] = period.previousYear.check;
        dataPoint[`${comparisonData.previousYear}_creditCard`] = period.previousYear.creditCard;
        dataPoint[`${comparisonData.currentYear}_cash`] = period.currentYear.cash;
        dataPoint[`${comparisonData.currentYear}_check`] = period.currentYear.check;
        dataPoint[`${comparisonData.currentYear}_creditCard`] = period.currentYear.creditCard;
      } else {
        // Simple bars without payment type breakdown
        dataPoint[`${comparisonData.previousYear}`] = period.previousYear.total;
        dataPoint[`${comparisonData.currentYear}`] = period.currentYear.total;
      }

      // Store totals for tooltip
      dataPoint.previousYearTotal = period.previousYear.total;
      dataPoint.currentYearTotal = period.currentYear.total;
      dataPoint.previousYearData = period.previousYear;
      dataPoint.currentYearData = period.currentYear;

      return dataPoint;
    });
  };

  const getTherapistName = () => {
    if (!selectedTherapist) return 'All Therapists';
    const therapist = therapists.find(t => t.id.toString() === selectedTherapist.toString());
    return therapist ? `${therapist.first_name} ${therapist.last_name}` : 'Selected Therapist';
  };

  // Custom tooltip for comparison chart
  const ComparisonTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    
    const dataPoint = payload[0]?.payload;
    if (!dataPoint) return null;

    const prevData = dataPoint.previousYearData;
    const currData = dataPoint.currentYearData;
    
    const percentChange = prevData.total > 0 
      ? ((currData.total - prevData.total) / prevData.total * 100).toFixed(1)
      : currData.total > 0 ? 100 : 0;

    return (
      <div style={tooltipContainerStyle}>
        <p style={tooltipTitleStyle}>{label}</p>
        
        <div style={tooltipSectionStyle}>
          <p style={{...tooltipYearStyle, color: 'var(--success-color)'}}>
            {comparisonData?.currentYear} (Current)
          </p>
          <p style={tooltipValueStyle}>Total: {formatCurrency(currData.total)}</p>
          {showPaymentTypes && (
            <div style={tooltipBreakdownStyle}>
              <span style={{...tooltipPaymentStyle, color: '#81C784'}}>Cash: {formatCurrency(currData.cash)}</span>
              <span style={{...tooltipPaymentStyle, color: 'var(--success-color)'}}>Check: {formatCurrency(currData.check)}</span>
              <span style={{...tooltipPaymentStyle, color: '#2E7D32'}}>Credit: {formatCurrency(currData.creditCard)}</span>
            </div>
          )}
        </div>

        <div style={{...tooltipSectionStyle, borderTop: '1px solid var(--border-color)', paddingTop: '8px'}}>
          <p style={{...tooltipYearStyle, color: '#2196F3'}}>
            {comparisonData?.previousYear} (Previous)
          </p>
          <p style={tooltipValueStyle}>Total: {formatCurrency(prevData.total)}</p>
          {showPaymentTypes && (
            <div style={tooltipBreakdownStyle}>
              <span style={{...tooltipPaymentStyle, color: '#90CAF9'}}>Cash: {formatCurrency(prevData.cash)}</span>
              <span style={{...tooltipPaymentStyle, color: '#2196F3'}}>Check: {formatCurrency(prevData.check)}</span>
              <span style={{...tooltipPaymentStyle, color: '#1565C0'}}>Credit: {formatCurrency(prevData.creditCard)}</span>
            </div>
          )}
        </div>

        <div style={tooltipChangeStyle}>
          <span style={{
            color: parseFloat(percentChange) >= 0 ? 'var(--success-color)' : '#f44336',
            fontWeight: 'bold'
          }}>
            {parseFloat(percentChange) >= 0 ? '↑' : '↓'} {Math.abs(percentChange)}% YoY
          </span>
        </div>
      </div>
    );
  };

  // Custom legend for comparison chart
  const renderComparisonLegend = () => {
    if (!comparisonData) return null;

    if (showPaymentTypes) {
      return (
        <div style={legendContainerStyle}>
          <div style={legendGroupStyle}>
            <span style={legendGroupTitleStyle}>{comparisonData.previousYear}</span>
            <div style={legendItemsStyle}>
              <span style={{...legendItemStyle, backgroundColor: '#90CAF9'}}>Cash</span>
              <span style={{...legendItemStyle, backgroundColor: '#2196F3'}}>Check</span>
              <span style={{...legendItemStyle, backgroundColor: '#1565C0'}}>Credit</span>
            </div>
          </div>
          <div style={legendGroupStyle}>
            <span style={legendGroupTitleStyle}>{comparisonData.currentYear}</span>
            <div style={legendItemsStyle}>
              <span style={{...legendItemStyle, backgroundColor: '#81C784'}}>Cash</span>
              <span style={{...legendItemStyle, backgroundColor: 'var(--success-color)'}}>Check</span>
              <span style={{...legendItemStyle, backgroundColor: '#2E7D32'}}>Credit</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={legendContainerStyle}>
        <span style={{...legendSimpleStyle, backgroundColor: '#2196F3'}}>{comparisonData.previousYear}</span>
        <span style={{...legendSimpleStyle, backgroundColor: 'var(--success-color)'}}>{comparisonData.currentYear}</span>
      </div>
    );
  };

  return (
    <>
      <div style={cardStyle}>
        <div style={cardHeaderStyle}>
          <h3 style={cardTitleStyle}>Revenue Summary</h3>
          
          <select 
            style={selectStyle} 
            value={selectedTherapist || 'all'} 
            onChange={handleTherapistChange}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="all">All Therapists</option>
            {therapists.map(therapist => (
              <option key={therapist.id} value={therapist.id}>
                {therapist.first_name} {therapist.last_name}
              </option>
            ))}
          </select>
        </div>
        
        {loading ? (
          <div style={loadingStyle}>Loading revenue data...</div>
        ) : (
          <div style={statsContainerStyle} onClick={handleCardClick}>
            <div style={statItemStyle}>
              <span style={statLabelStyle}>Today</span>
              <div style={revenueStyle}>
                <div style={revenueItemStyle}>
                  <span style={revenueLabelStyle}>Billed:</span>
                  <span style={revenueValueStyle}>{formatCurrency(summaryData.today.billed)}</span>
                </div>
                <div style={revenueItemStyle}>
                  <span style={revenueLabelStyle}>Collected:</span>
                  <span style={revenueValueStyle}>{formatCurrency(summaryData.today.collected)}</span>
                </div>
              </div>
            </div>
            <div style={statItemStyle}>
              <span style={statLabelStyle}>This Week</span>
              <div style={revenueStyle}>
                <div style={revenueItemStyle}>
                  <span style={revenueLabelStyle}>Billed:</span>
                  <span style={revenueValueStyle}>{formatCurrency(summaryData.week.billed)}</span>
                </div>
                <div style={revenueItemStyle}>
                  <span style={revenueLabelStyle}>Collected:</span>
                  <span style={revenueValueStyle}>{formatCurrency(summaryData.week.collected)}</span>
                </div>
              </div>
            </div>
            <div style={statItemStyle}>
              <span style={statLabelStyle}>This Month</span>
              <div style={revenueStyle}>
                <div style={revenueItemStyle}>
                  <span style={revenueLabelStyle}>Billed:</span>
                  <span style={revenueValueStyle}>{formatCurrency(summaryData.month.billed)}</span>
                </div>
                <div style={revenueItemStyle}>
                  <span style={revenueLabelStyle}>Collected:</span>
                  <span style={revenueValueStyle}>{formatCurrency(summaryData.month.collected)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div style={cardActionsStyle}>
          <span style={clickInstructionStyle}>Click for historical data</span>
          <button 
            style={comparisonButtonStyle}
            onClick={(e) => {
              e.stopPropagation();
              fetchComparisonData('month');
            }}
          >
            📊 Year-over-Year
          </button>
        </div>
      </div>

      {/* Standard History Modal */}
      {showHistoryModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={modalHeaderStyle}>
              <h2 style={modalTitleStyle}>
                Revenue History - {getTherapistName()}
              </h2>
              <div style={modalActionsStyle}>
                <button 
                  onClick={toggleGraph} 
                  style={toggleButtonStyle}
                >
                  {showGraph ? 'Show Table' : 'Show Graph'}
                </button>
                <button 
                  onClick={() => setShowHistoryModal(false)}
                  style={modalCloseButtonStyle}
                >
                  &times;
                </button>
              </div>
            </div>

            <div style={modalBodyStyle}>
              <div style={periodTabsStyle}>
                <button 
                  style={periodType === 'day' ? activeTabStyle : tabStyle}
                  onClick={() => fetchHistoryData('day')}
                >
                  Daily
                </button>
                <button 
                  style={periodType === 'week' ? activeTabStyle : tabStyle}
                  onClick={() => fetchHistoryData('week')}
                >
                  Weekly
                </button>
                <button 
                  style={periodType === 'month' ? activeTabStyle : tabStyle}
                  onClick={() => fetchHistoryData('month')}
                >
                  Monthly
                </button>
              </div>

              <div style={filterContainerStyle}>
                <select 
                  style={{...selectStyle, marginRight: '1rem'}} 
                  value={selectedTherapist || 'all'} 
                  onChange={handleTherapistChange}
                >
                  <option value="all">All Therapists</option>
                  {therapists.map(therapist => (
                    <option key={therapist.id} value={therapist.id}>
                      {therapist.first_name} {therapist.last_name}
                    </option>
                  ))}
                </select>

                <button 
                  onClick={toggleViewType} 
                  style={viewToggleStyle}
                >
                  View {viewType === 'billed' ? 'Collected' : 'Billed'} Revenue
                </button>
              </div>

              {historyLoading ? (
                <div style={loadingStyle}>Loading history data...</div>
              ) : (
                <>
                  {showGraph ? (
                    <div style={graphContainerStyle}>
                      {historyData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart
                            data={prepareChartData()}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="name" 
                              tick={{ fontSize: 12 }} 
                              interval={periodType === 'day' ? 2 : 0} 
                            />
                            <YAxis tickFormatter={(value) => formatCurrencyK(value)} />
                            <Tooltip 
                              formatter={(value) => formatCurrency(value)} 
                              labelFormatter={(label) => `Period: ${label}`}
                            />
                            <Legend />
                            <Bar
                              dataKey={viewType}
                              name={viewType === 'billed' ? 'Billed Amount' : 'Collected Amount'}
                              fill={viewType === 'billed' ? '#ff7f0e' : 'var(--success-color)'}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div style={{textAlign: 'center'}}>No data available for graphing</div>
                      )}
                    </div>
                  ) : (
                    <div style={historyContainerStyle}>
                      <p style={{fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem', textAlign: 'center'}}>
                        Click any row to see session details
                      </p>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Period</th>
                            <th style={thStyle}>{viewType === 'billed' ? 'Billed Amount' : 'Collected Amount'}</th>
                            <th style={thStyle}>Sessions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyData.map((item, index) => (
                            <tr 
                              key={index} 
                              style={{
                                ...(index % 2 === 0 ? {} : trAlternateStyle),
                                cursor: item.sessions > 0 ? 'pointer' : 'default',
                                transition: 'background-color 0.2s'
                              }}
                              onClick={() => item.sessions > 0 && fetchPeriodDetails(item)}
                              onMouseEnter={(e) => {
                                if (item.sessions > 0) {
                                  e.currentTarget.style.backgroundColor = '#e8f4fd';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = index % 2 === 0 ? '' : 'var(--surface-muted)';
                              }}
                            >
                              <td style={{...tdStyle, color: item.sessions > 0 ? '#3498db' : 'inherit'}}>
                                {getPeriodLabel(item.period)}
                                {item.sessions > 0 && <span style={{marginLeft: '0.5rem', fontSize: '0.8rem'}}>🔍</span>}
                              </td>
                              <td style={{...tdStyle, textAlign: 'right'}}>{formatCurrency(viewType === 'billed' ? item.billed : item.collected)}</td>
                              <td style={{...tdStyle, textAlign: 'center'}}>{item.sessions}</td>
                            </tr>
                          ))}
                          {historyData.length === 0 && (
                            <tr>
                              <td colSpan="3" style={{...tdStyle, textAlign: 'center'}}>
                                No data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                        {historyData.length > 0 && (
                          <tfoot>
                            <tr style={{fontWeight: 'bold', backgroundColor: '#f0fff0'}}>
                              <td style={tdStyle}>Total</td>
                              <td style={{...tdStyle, textAlign: 'right'}}>
                                {formatCurrency(
                                  historyData.reduce((sum, item) => 
                                    sum + (viewType === 'billed' ? item.billed : item.collected), 0)
                                )}
                              </td>
                              <td style={{...tdStyle, textAlign: 'center'}}>
                                {historyData.reduce((sum, item) => sum + item.sessions, 0)}
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* NEW: Year-over-Year Comparison Modal */}
      {showComparisonModal && (
        <div style={modalOverlayStyle}>
          <div style={{...modalContentStyle, maxWidth: '1000px'}}>
            <div style={modalHeaderStyle}>
              <h2 style={modalTitleStyle}>
                Year-over-Year Revenue Comparison - {getTherapistName()}
              </h2>
              <div style={modalActionsStyle}>
                <button 
                  onClick={() => setShowComparisonModal(false)}
                  style={modalCloseButtonStyle}
                >
                  &times;
                </button>
              </div>
            </div>

            <div style={modalBodyStyle}>
              {/* Period Type Tabs */}
              <div style={periodTabsStyle}>
                <button 
                  style={comparisonType === 'week' ? activeTabStyle : tabStyle}
                  onClick={() => fetchComparisonData('week')}
                >
                  Weekly
                </button>
                <button 
                  style={comparisonType === 'month' ? activeTabStyle : tabStyle}
                  onClick={() => fetchComparisonData('month')}
                >
                  Monthly
                </button>
                <button 
                  style={comparisonType === 'quarter' ? activeTabStyle : tabStyle}
                  onClick={() => fetchComparisonData('quarter')}
                >
                  Quarterly
                </button>
              </div>

              {/* Filters Row */}
              <div style={filterContainerStyle}>
                <select 
                  style={{...selectStyle, marginRight: '1rem'}} 
                  value={selectedTherapist || 'all'} 
                  onChange={handleTherapistChange}
                >
                  <option value="all">All Therapists</option>
                  {therapists.map(therapist => (
                    <option key={therapist.id} value={therapist.id}>
                      {therapist.first_name} {therapist.last_name}
                    </option>
                  ))}
                </select>

                <label style={checkboxLabelStyle}>
                  <input 
                    type="checkbox" 
                    checked={showPaymentTypes} 
                    onChange={(e) => setShowPaymentTypes(e.target.checked)}
                    style={checkboxStyle}
                  />
                  Show Payment Types
                </label>
              </div>

              {comparisonLoading ? (
                <div style={loadingStyle}>Loading comparison data...</div>
              ) : comparisonData ? (
                <>
                  {/* Summary Cards */}
                  <div style={summaryCardsStyle}>
                    <div style={{...summaryCardStyle, borderLeft: '4px solid #2196F3'}}>
                      <h4 style={summaryCardTitleStyle}>{comparisonData.previousYear} Total</h4>
                      <p style={summaryCardValueStyle}>{formatCurrency(comparisonData.totals.previousYear.total)}</p>
                      {showPaymentTypes && (
                        <div style={summaryCardBreakdownStyle}>
                          <span>Cash: {formatCurrency(comparisonData.totals.previousYear.cash)}</span>
                          <span>Check: {formatCurrency(comparisonData.totals.previousYear.check)}</span>
                          <span>Credit: {formatCurrency(comparisonData.totals.previousYear.creditCard)}</span>
                        </div>
                      )}
                    </div>
                    <div style={{...summaryCardStyle, borderLeft: '4px solid var(--success-color)'}}>
                      <h4 style={summaryCardTitleStyle}>{comparisonData.currentYear} Total</h4>
                      <p style={summaryCardValueStyle}>{formatCurrency(comparisonData.totals.currentYear.total)}</p>
                      {showPaymentTypes && (
                        <div style={summaryCardBreakdownStyle}>
                          <span>Cash: {formatCurrency(comparisonData.totals.currentYear.cash)}</span>
                          <span>Check: {formatCurrency(comparisonData.totals.currentYear.check)}</span>
                          <span>Credit: {formatCurrency(comparisonData.totals.currentYear.creditCard)}</span>
                        </div>
                      )}
                    </div>
                    <div style={{...summaryCardStyle, borderLeft: '4px solid #FF9800'}}>
                      <h4 style={summaryCardTitleStyle}>YoY Change</h4>
                      <p style={{
                        ...summaryCardValueStyle,
                        color: comparisonData.totals.currentYear.total >= comparisonData.totals.previousYear.total ? 'var(--success-color)' : '#f44336'
                      }}>
                        {comparisonData.totals.previousYear.total > 0 
                          ? `${((comparisonData.totals.currentYear.total - comparisonData.totals.previousYear.total) / comparisonData.totals.previousYear.total * 100).toFixed(1)}%`
                          : 'N/A'}
                      </p>
                      <p style={summaryCardDiffStyle}>
                        {formatCurrency(comparisonData.totals.currentYear.total - comparisonData.totals.previousYear.total)}
                      </p>
                    </div>
                  </div>

                  {/* Custom Legend */}
                  {renderComparisonLegend()}

                  {/* Chart */}
                  <div style={{...graphContainerStyle, height: '400px'}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={prepareComparisonChartData()}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 12, fill: 'var(--text-muted)' }}
                          axisLine={{ stroke: 'var(--border-color)' }}
                        />
                        <YAxis 
                          tickFormatter={(value) => formatCurrencyK(value)}
                          tick={{ fontSize: 12, fill: 'var(--text-muted)' }}
                          axisLine={{ stroke: 'var(--border-color)' }}
                          label={{ 
                            value: '$ in thousands', 
                            angle: -90, 
                            position: 'insideLeft',
                            style: { textAnchor: 'middle', fill: 'var(--text-muted)', fontSize: 12 }
                          }}
                        />
                        <Tooltip content={<ComparisonTooltip />} />
                        
                        {showPaymentTypes ? (
                          <>
                            {/* Previous Year - Stacked Blues */}
                            <Bar 
                              dataKey={`${comparisonData.previousYear}_creditCard`} 
                              stackId="prev"
                              fill="#1565C0"
                              name={`${comparisonData.previousYear} Credit Card`}
                            />
                            <Bar 
                              dataKey={`${comparisonData.previousYear}_check`} 
                              stackId="prev"
                              fill="#2196F3"
                              name={`${comparisonData.previousYear} Check`}
                            />
                            <Bar 
                              dataKey={`${comparisonData.previousYear}_cash`} 
                              stackId="prev"
                              fill="#90CAF9"
                              name={`${comparisonData.previousYear} Cash`}
                            />
                            
                            {/* Current Year - Stacked Greens */}
                            <Bar 
                              dataKey={`${comparisonData.currentYear}_creditCard`} 
                              stackId="curr"
                              fill="#2E7D32"
                              name={`${comparisonData.currentYear} Credit Card`}
                            />
                            <Bar 
                              dataKey={`${comparisonData.currentYear}_check`} 
                              stackId="curr"
                              fill="var(--success-color)"
                              name={`${comparisonData.currentYear} Check`}
                            />
                            <Bar 
                              dataKey={`${comparisonData.currentYear}_cash`} 
                              stackId="curr"
                              fill="#81C784"
                              name={`${comparisonData.currentYear} Cash`}
                            />
                          </>
                        ) : (
                          <>
                            <Bar 
                              dataKey={`${comparisonData.previousYear}`}
                              fill="#2196F3"
                              name={`${comparisonData.previousYear}`}
                              radius={[4, 4, 0, 0]}
                            />
                            <Bar 
                              dataKey={`${comparisonData.currentYear}`}
                              fill="var(--success-color)"
                              name={`${comparisonData.currentYear}`}
                              radius={[4, 4, 0, 0]}
                            />
                          </>
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Data Table */}
                  <div style={{...historyContainerStyle, marginTop: '1.5rem'}}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Period</th>
                          <th style={{...thStyle, backgroundColor: '#E3F2FD', textAlign: 'right'}}>
                            {comparisonData.previousYear}
                          </th>
                          <th style={{...thStyle, backgroundColor: '#E8F5E9', textAlign: 'right'}}>
                            {comparisonData.currentYear}
                          </th>
                          <th style={{...thStyle, textAlign: 'center'}}>Change</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonData.periods.map((period, index) => {
                          const change = period.previousYear.total > 0 
                            ? ((period.currentYear.total - period.previousYear.total) / period.previousYear.total * 100).toFixed(1)
                            : period.currentYear.total > 0 ? 100 : 0;
                          
                          return (
                            <tr key={index} style={index % 2 === 0 ? {} : trAlternateStyle}>
                              <td style={tdStyle}>{period.label}</td>
                              <td style={{...tdStyle, textAlign: 'right', backgroundColor: 'rgba(33, 150, 243, 0.05)'}}>
                                {formatCurrency(period.previousYear.total)}
                              </td>
                              <td style={{...tdStyle, textAlign: 'right', backgroundColor: 'rgba(76, 175, 80, 0.05)'}}>
                                {formatCurrency(period.currentYear.total)}
                              </td>
                              <td style={{
                                ...tdStyle, 
                                textAlign: 'center',
                                color: parseFloat(change) >= 0 ? 'var(--success-color)' : '#f44336',
                                fontWeight: '500'
                              }}>
                                {parseFloat(change) >= 0 ? '+' : ''}{change}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{fontWeight: 'bold', backgroundColor: 'var(--surface-muted)'}}>
                          <td style={tdStyle}>Total</td>
                          <td style={{...tdStyle, textAlign: 'right'}}>
                            {formatCurrency(comparisonData.totals.previousYear.total)}
                          </td>
                          <td style={{...tdStyle, textAlign: 'right'}}>
                            {formatCurrency(comparisonData.totals.currentYear.total)}
                          </td>
                          <td style={{
                            ...tdStyle, 
                            textAlign: 'center',
                            color: comparisonData.totals.currentYear.total >= comparisonData.totals.previousYear.total ? 'var(--success-color)' : '#f44336'
                          }}>
                            {comparisonData.totals.previousYear.total > 0 
                              ? `${((comparisonData.totals.currentYear.total - comparisonData.totals.previousYear.total) / comparisonData.totals.previousYear.total * 100).toFixed(1)}%`
                              : 'N/A'}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              ) : (
                <div style={{textAlign: 'center', padding: '2rem'}}>
                  No comparison data available
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* NEW: Period Detail Modal */}
      {showDetailModal && (
        <div style={modalOverlayStyle}>
          <div style={{...modalContentStyle, maxWidth: '1100px'}}>
            <div style={modalHeaderStyle}>
              <h2 style={modalTitleStyle}>
                Session Details - {selectedPeriodLabel}
              </h2>
              <div style={modalActionsStyle}>
                <button 
                  onClick={() => setShowDetailModal(false)}
                  style={modalCloseButtonStyle}
                >
                  &times;
                </button>
              </div>
            </div>

            <div style={modalBodyStyle}>
              {detailLoading ? (
                <div style={loadingStyle}>Loading session details...</div>
              ) : detailData ? (
                <>
                  {/* Summary Cards */}
                  <div style={detailSummaryCardsStyle}>
                    <div style={detailSummaryCardStyle}>
                      <span style={detailSummaryLabelStyle}>Total Sessions</span>
                      <span style={detailSummaryValueStyle}>{detailData.summary.totalSessions}</span>
                    </div>
                    <div style={detailSummaryCardStyle}>
                      <span style={detailSummaryLabelStyle}>Total Billed</span>
                      <span style={{...detailSummaryValueStyle, color: '#e67e22'}}>{formatCurrency(detailData.summary.totalBilled)}</span>
                    </div>
                    <div style={detailSummaryCardStyle}>
                      <span style={detailSummaryLabelStyle}>Total Collected</span>
                      <span style={{...detailSummaryValueStyle, color: 'var(--success-color)'}}>{formatCurrency(detailData.summary.totalCollected)}</span>
                    </div>
                    <div style={detailSummaryCardStyle}>
                      <span style={detailSummaryLabelStyle}>Avg per Session</span>
                      <span style={detailSummaryValueStyle}>{formatCurrency(detailData.summary.avgBilled)}</span>
                    </div>
                  </div>

                  {/* View Tabs */}
                  <div style={periodTabsStyle}>
                    <button 
                      style={detailViewTab === 'sessions' ? activeTabStyle : tabStyle}
                      onClick={() => setDetailViewTab('sessions')}
                    >
                      All Sessions ({detailData.sessions.length})
                    </button>
                    <button 
                      style={detailViewTab === 'byTherapist' ? activeTabStyle : tabStyle}
                      onClick={() => setDetailViewTab('byTherapist')}
                    >
                      By Therapist ({detailData.byTherapist.length})
                    </button>
                    <button 
                      style={detailViewTab === 'byServiceType' ? activeTabStyle : tabStyle}
                      onClick={() => setDetailViewTab('byServiceType')}
                    >
                      By Service Type ({detailData.byServiceType.length})
                    </button>
                  </div>

                  {/* Sessions Tab */}
                  {detailViewTab === 'sessions' && (
                    <div style={{...historyContainerStyle, maxHeight: '450px'}}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Date</th>
                            <th style={thStyle}>Client</th>
                            <th style={thStyle}>Therapist</th>
                            <th style={thStyle}>Service Type</th>
                            <th style={thStyle}>Billed</th>
                            <th style={thStyle}>Collected</th>
                            <th style={thStyle}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailData.sessions.map((session, index) => (
                            <tr key={session.id} style={index % 2 === 0 ? {} : trAlternateStyle}>
                              <td style={tdStyle}>
                                {new Date(session.sessionDate).toLocaleDateString()}
                              </td>
                              <td style={tdStyle}>{session.clientName}</td>
                              <td style={tdStyle}>{session.therapistName}</td>
                              <td style={{...tdStyle, fontSize: '0.85rem'}}>
                                {session.serviceType || 'N/A'}
                              </td>
                              <td style={{...tdStyle, textAlign: 'right', color: '#e67e22'}}>
                                {formatCurrency(session.billedAmount)}
                              </td>
                              <td style={{...tdStyle, textAlign: 'right', color: 'var(--success-color)'}}>
                                {formatCurrency(session.collectedAmount)}
                              </td>
                              <td style={tdStyle}>
                                <span style={{
                                  ...statusBadgeStyle,
                                  backgroundColor: session.status === 'PAID' ? 'var(--success-color)' : 
                                                   session.status === 'DEBT' ? '#e74c3c' : 
                                                   session.status === 'SUBMITTED' ? '#3498db' : '#95a5a6'
                                }}>
                                  {session.noShow ? 'No Show' : session.cancelation ? 'Cancelled' : session.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                          {detailData.sessions.length === 0 && (
                            <tr>
                              <td colSpan="7" style={{...tdStyle, textAlign: 'center'}}>
                                No sessions found for this period
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* By Therapist Tab */}
                  {detailViewTab === 'byTherapist' && (
                    <div style={historyContainerStyle}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Therapist</th>
                            <th style={thStyle}>Sessions</th>
                            <th style={thStyle}>Total Billed</th>
                            <th style={thStyle}>Total Collected</th>
                            <th style={thStyle}>Avg per Session</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailData.byTherapist.map((item, index) => (
                            <tr key={item.therapistId} style={index % 2 === 0 ? {} : trAlternateStyle}>
                              <td style={{...tdStyle, fontWeight: '500'}}>{item.therapistName}</td>
                              <td style={{...tdStyle, textAlign: 'center'}}>{item.sessionCount}</td>
                              <td style={{...tdStyle, textAlign: 'right', color: '#e67e22'}}>
                                {formatCurrency(item.totalBilled)}
                              </td>
                              <td style={{...tdStyle, textAlign: 'right', color: 'var(--success-color)'}}>
                                {formatCurrency(item.totalCollected)}
                              </td>
                              <td style={{...tdStyle, textAlign: 'right'}}>
                                {formatCurrency(item.avgBilled)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{fontWeight: 'bold', backgroundColor: '#f0fff0'}}>
                            <td style={tdStyle}>Total</td>
                            <td style={{...tdStyle, textAlign: 'center'}}>
                              {detailData.byTherapist.reduce((sum, t) => sum + t.sessionCount, 0)}
                            </td>
                            <td style={{...tdStyle, textAlign: 'right', color: '#e67e22'}}>
                              {formatCurrency(detailData.byTherapist.reduce((sum, t) => sum + t.totalBilled, 0))}
                            </td>
                            <td style={{...tdStyle, textAlign: 'right', color: 'var(--success-color)'}}>
                              {formatCurrency(detailData.byTherapist.reduce((sum, t) => sum + t.totalCollected, 0))}
                            </td>
                            <td style={{...tdStyle, textAlign: 'right'}}>
                              {formatCurrency(detailData.summary.avgBilled)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}

                  {/* By Service Type Tab */}
                  {detailViewTab === 'byServiceType' && (
                    <div style={historyContainerStyle}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Service Type</th>
                            <th style={thStyle}>Sessions</th>
                            <th style={thStyle}>Total Billed</th>
                            <th style={thStyle}>Total Collected</th>
                            <th style={thStyle}>Avg per Session</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailData.byServiceType.map((item, index) => (
                            <tr key={item.serviceType} style={index % 2 === 0 ? {} : trAlternateStyle}>
                              <td style={{...tdStyle, fontWeight: '500'}}>{item.serviceType}</td>
                              <td style={{...tdStyle, textAlign: 'center'}}>{item.sessionCount}</td>
                              <td style={{...tdStyle, textAlign: 'right', color: '#e67e22'}}>
                                {formatCurrency(item.totalBilled)}
                              </td>
                              <td style={{...tdStyle, textAlign: 'right', color: 'var(--success-color)'}}>
                                {formatCurrency(item.totalCollected)}
                              </td>
                              <td style={{...tdStyle, textAlign: 'right'}}>
                                {formatCurrency(item.avgBilled)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{fontWeight: 'bold', backgroundColor: '#f0fff0'}}>
                            <td style={tdStyle}>Total</td>
                            <td style={{...tdStyle, textAlign: 'center'}}>
                              {detailData.byServiceType.reduce((sum, t) => sum + t.sessionCount, 0)}
                            </td>
                            <td style={{...tdStyle, textAlign: 'right', color: '#e67e22'}}>
                              {formatCurrency(detailData.byServiceType.reduce((sum, t) => sum + t.totalBilled, 0))}
                            </td>
                            <td style={{...tdStyle, textAlign: 'right', color: 'var(--success-color)'}}>
                              {formatCurrency(detailData.byServiceType.reduce((sum, t) => sum + t.totalCollected, 0))}
                            </td>
                            <td style={{...tdStyle, textAlign: 'right'}}>
                              {formatCurrency(detailData.summary.avgBilled)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <div style={{textAlign: 'center', padding: '2rem'}}>
                  No data available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================================
// STYLES
// ============================================================================

const cardStyle = {
  flex: '1 1 400px',
  minWidth: '350px',
  maxWidth: '550px',
  padding: '1.5rem',
  backgroundColor: '#f0fff0',
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  cursor: 'pointer',
};

const cardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem'
};

const cardTitleStyle = {
  margin: 0,
  fontSize: '1.3rem',
  color: 'var(--primary-color)'
};

const selectStyle = {
  padding: '0.3rem 0.5rem',
  borderRadius: '4px',
  border: '1px solid var(--border-color)',
  fontSize: '0.85rem',
  color: 'var(--primary-color)',
  backgroundColor: 'white',
  cursor: 'pointer'
};

const statsContainerStyle = {
  display: 'flex',
  justifyContent: 'space-around',
  marginBottom: '1rem'
};

const statItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
};

const statLabelStyle = {
  fontSize: '0.9rem',
  color: 'var(--text-muted)',
  marginBottom: '0.5rem'
};

const revenueStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start'
};

const revenueItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '0.25rem'
};

const revenueLabelStyle = {
  fontSize: '0.8rem',
  color: 'var(--text-muted)',
  marginRight: '0.5rem'
};

const revenueValueStyle = {
  fontSize: '0.9rem',
  fontWeight: 'bold',
  color: 'var(--success-color)'
};

const loadingStyle = {
  textAlign: 'center',
  padding: '1rem',
  color: 'var(--text-muted)'
};

const cardActionsStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '0.5rem'
};

const clickInstructionStyle = {
  fontSize: '0.8rem',
  color: '#95a5a6'
};

const comparisonButtonStyle = {
  padding: '0.4rem 0.8rem',
  backgroundColor: '#3498db',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  fontSize: '0.8rem',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
};

// Modal Styles
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  width: '90%',
  maxWidth: '800px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
};

const modalHeaderStyle = {
  padding: '1.5rem',
  borderBottom: '1px solid var(--border-color)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const modalTitleStyle = {
  margin: 0,
  fontSize: '1.5rem',
};

const modalActionsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem'
};

const toggleButtonStyle = {
  background: 'var(--success-color)',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  padding: '0.5rem 1rem',
  fontSize: '0.9rem',
  cursor: 'pointer'
};

const modalCloseButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '1.5rem',
  cursor: 'pointer',
  color: 'var(--text-muted)',
};

const modalBodyStyle = {
  padding: '1.5rem',
};

const periodTabsStyle = {
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '1.5rem',
  borderBottom: '1px solid var(--border-color)',
  paddingBottom: '0.5rem'
};

const tabStyle = {
  padding: '0.5rem 1rem',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: 'var(--text-muted)',
  fontSize: '1rem',
  margin: '0 0.5rem',
  borderRadius: '4px'
};

const activeTabStyle = {
  ...tabStyle,
  backgroundColor: 'var(--success-color)',
  color: 'white',
  fontWeight: 'bold'
};

const viewToggleStyle = {
  padding: '0.5rem 1rem',
  backgroundColor: '#3498db',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.9rem'
};

const filterContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem'
};

const checkboxLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  fontSize: '0.9rem',
  color: 'var(--primary-color)',
  cursor: 'pointer'
};

const checkboxStyle = {
  marginRight: '0.5rem',
  cursor: 'pointer'
};

const historyContainerStyle = {
  maxHeight: '400px',
  overflow: 'auto'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse'
};

const thStyle = {
  backgroundColor: 'var(--surface-muted)',
  padding: '0.75rem',
  textAlign: 'left',
  borderBottom: '2px solid var(--border-color)',
  position: 'sticky',
  top: 0
};

const tdStyle = {
  padding: '0.75rem',
  borderBottom: '1px solid var(--border-color)'
};

const trAlternateStyle = {
  backgroundColor: 'var(--surface-muted)'
};

const graphContainerStyle = {
  width: '100%',
  height: '350px',
  marginTop: '1rem'
};

// Summary Cards Styles
const summaryCardsStyle = {
  display: 'flex',
  gap: '1rem',
  marginBottom: '1.5rem',
  flexWrap: 'wrap'
};

const summaryCardStyle = {
  flex: '1 1 200px',
  padding: '1rem',
  backgroundColor: 'var(--surface-muted)',
  borderRadius: '8px',
  minWidth: '180px'
};

const summaryCardTitleStyle = {
  margin: '0 0 0.5rem 0',
  fontSize: '0.9rem',
  color: 'var(--text-muted)'
};

const summaryCardValueStyle = {
  margin: '0 0 0.5rem 0',
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: 'var(--primary-color)'
};

const summaryCardBreakdownStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  fontSize: '0.75rem',
  color: 'var(--text-muted)'
};

const summaryCardDiffStyle = {
  margin: 0,
  fontSize: '0.85rem',
  color: 'var(--text-muted)'
};

// Legend Styles
const legendContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  gap: '2rem',
  marginBottom: '1rem',
  padding: '0.75rem',
  backgroundColor: 'var(--surface-muted)',
  borderRadius: '8px'
};

const legendGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.5rem'
};

const legendGroupTitleStyle = {
  fontWeight: 'bold',
  fontSize: '0.9rem',
  color: 'var(--primary-color)'
};

const legendItemsStyle = {
  display: 'flex',
  gap: '0.5rem'
};

const legendItemStyle = {
  padding: '0.25rem 0.5rem',
  borderRadius: '4px',
  fontSize: '0.75rem',
  color: 'white'
};

const legendSimpleStyle = {
  padding: '0.35rem 0.75rem',
  borderRadius: '4px',
  fontSize: '0.85rem',
  color: 'white',
  fontWeight: '500'
};

// Tooltip Styles
const tooltipContainerStyle = {
  backgroundColor: 'white',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  padding: '12px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  minWidth: '200px'
};

const tooltipTitleStyle = {
  margin: '0 0 10px 0',
  fontWeight: 'bold',
  fontSize: '1rem',
  borderBottom: '1px solid var(--border-color)',
  paddingBottom: '8px'
};

const tooltipSectionStyle = {
  marginBottom: '8px'
};

const tooltipYearStyle = {
  margin: '0 0 4px 0',
  fontWeight: '600',
  fontSize: '0.85rem'
};

const tooltipValueStyle = {
  margin: '0 0 4px 0',
  fontSize: '1.1rem',
  fontWeight: 'bold',
  color: 'var(--primary-color)'
};

const tooltipBreakdownStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  marginTop: '4px'
};

const tooltipPaymentStyle = {
  fontSize: '0.8rem'
};

const tooltipChangeStyle = {
  marginTop: '10px',
  paddingTop: '10px',
  borderTop: '1px solid var(--border-color)',
  textAlign: 'center'
};

// Detail Modal Styles
const detailSummaryCardsStyle = {
  display: 'flex',
  gap: '1rem',
  marginBottom: '1.5rem',
  flexWrap: 'wrap'
};

const detailSummaryCardStyle = {
  flex: '1 1 150px',
  padding: '1rem',
  backgroundColor: 'var(--surface-muted)',
  borderRadius: '8px',
  textAlign: 'center',
  minWidth: '120px'
};

const detailSummaryLabelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  color: 'var(--text-muted)',
  marginBottom: '0.5rem'
};

const detailSummaryValueStyle = {
  display: 'block',
  fontSize: '1.4rem',
  fontWeight: 'bold',
  color: 'var(--primary-color)'
};

const statusBadgeStyle = {
  padding: '0.25rem 0.5rem',
  borderRadius: '4px',
  fontSize: '0.75rem',
  color: 'white',
  fontWeight: '500'
};

export default RevenueSummaryCard;
